/**
 * `<MeAuditTickerSection/>` — member-side audit ticker for the persistent right panel.
 *
 * Renders the 3 most-recent rows from `GET /api/account/me/audit` with
 * a "See full activity →" deep link to `/me/audit`.
 *
 * **Always-on** per #193's mapping table — the member's own audit
 * surface is a worker-trust pillar (`feedback_privacy_assurance_for_team_usage.md`),
 * not gated on a registry component. No `<ComponentGate>` wrap.
 *
 * **Cache-share.** Reuses `meAuditKey(accountId)` so the same fetch
 * the full `<MeAudit/>` page issues populates this section's data on
 * navigation back-and-forth (and vice-versa).
 *
 * **Role-aware row labels.** Target rows (LL-D4: written by a lead
 * about the caller) voice as "Your team lead ..."; actor rows voice
 * as "You ..." — same `summariseMeAuditEvent` the full page uses.
 *
 * **Focal-component discipline.** The section's focal element is the
 * row list; the header + deep-link are calm supporting context.
 */
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, History } from "lucide-react";
import { useMemo } from "react";
import { Link } from "react-router-dom";

import { apiFetch } from "../api/client";
import type { components } from "../api/schema.gen";
import { useAuth } from "../lib/AuthContext";
import {
  formatAbsolute,
  formatRelativeTime,
  isMeAuditTargetRow,
  summariseMeAuditEvent,
} from "../lib/meAuditFormat";
import { meAuditKey } from "../lib/meAuditKey";
import { AmbientErrorNotice, isUnexpectedApiError } from "./AmbientErrorNotice";
import { Skeleton } from "./ui";

type AuditListResponse = components["schemas"]["AuditListResponse"];
type AuditEventResponse = components["schemas"]["AuditEventResponse"];

const TICKER_ROW_LIMIT = 3;

export function MeAuditTickerSection() {
  const { accountId } = useAuth();
  const queryKey = useMemo(() => meAuditKey(accountId), [accountId]);

  const auditQuery = useQuery({
    queryKey,
    enabled: Boolean(accountId),
    queryFn: () => apiFetch<AuditListResponse>("/api/account/me/audit"),
  });

  const events = useMemo<AuditEventResponse[]>(() => {
    const raw = auditQuery.data?.events ?? [];
    return [...raw]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, TICKER_ROW_LIMIT);
  }, [auditQuery.data]);

  // An unexpected error (broken session / server fault) surfaces a calm
  // notice rather than vanishing — a missing activity ticker must not
  // mask a 401. An expected 404 keeps hiding.
  if (auditQuery.isError) {
    if (isUnexpectedApiError(auditQuery.error)) {
      return (
        <AmbientErrorNotice
          onRetry={() => void auditQuery.refetch()}
          label="Recent activity"
          testId="me-audit-ticker-section-error"
        />
      );
    }
    return null;
  }

  return (
    <section
      data-testid="me-audit-ticker-section"
      className="flex flex-col gap-2 px-1 py-2"
      aria-label="Your recent activity"
    >
      <header className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-fg">
          <History className="h-3.5 w-3.5 text-fg-subtle" aria-hidden="true" />
          Recent activity
        </div>
        <Link
          to="/me/audit"
          className="flex items-center gap-1 text-xs text-fg-muted hover:text-fg"
          data-testid="me-audit-ticker-deep-link"
        >
          See full activity
          <ArrowRight className="h-3 w-3" aria-hidden="true" />
        </Link>
      </header>

      {auditQuery.isLoading ? (
        <div className="flex flex-col gap-1.5" data-testid="me-audit-ticker-loading">
          <Skeleton className="h-8" />
          <Skeleton className="h-8" />
        </div>
      ) : events.length === 0 ? (
        <p className="text-xs text-fg-muted" data-testid="me-audit-ticker-empty">
          No changes yet — your activity will show up here.
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5" data-testid="me-audit-ticker-list">
          {events.map((event) => {
            const summary = summariseMeAuditEvent(event);
            const target = isMeAuditTargetRow(event);
            return (
              <li
                key={event.id}
                data-testid={`me-audit-ticker-row-${event.id}`}
                data-role={target ? "target" : "actor"}
                className="flex flex-col gap-0.5 rounded-sm bg-bg-elevated/60 p-2"
              >
                <p className="text-xs font-medium leading-tight text-fg">{summary.label}</p>
                <span
                  className="text-[10px] uppercase tracking-wide text-fg-subtle"
                  title={formatAbsolute(event.created_at)}
                >
                  {formatRelativeTime(event.created_at)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
